'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';

import { getBingUetBootstrapScriptContents, getDataGrailManagedScripts } from '@/src/lib/datagrail/scripts';
import { isSeoOnlyPathname } from '@/src/lib/seo/state-pages';

type Props = {
  /** Matches RootHeadScripts: with a GA4 loader on the page, skip the Floodlight gtag copy. */
  hasSharedGtagLoader: boolean;
};

/**
 * Rendered only when the SSR'd route was SEO-only, so the head shipped without
 * the marketing pixel set (HP-8781). The root layout persists across client
 * navigations, so without this a session that lands on a state hub would never
 * get Floodlight/Index/Ad Services/UET even after moving to monetized routes.
 * Latches on the first non-SEO route; DataGrail processes the late-inserted
 * text/plain tags the same way it does the UET bootstrap's runtime insert.
 */
export function LateMarketingScripts({ hasSharedGtagLoader }: Props) {
  const pathname = usePathname();
  const [latched, setLatched] = useState(false);

  useEffect(() => {
    if (!latched && !isSeoOnlyPathname(pathname)) setLatched(true);
  }, [latched, pathname]);

  if (!latched) return null;

  const marketingScripts = getDataGrailManagedScripts({
    comscoreConfigScriptContents: '',
    hasSharedGtagLoader,
  }).filter((script) => script.category === 'marketing');

  return (
    <>
      {marketingScripts.map((script) => {
        const props = {
          id: script.id,
          type: script.type,
          'data-category': script.category,
          'data-consent-id': script.consentId,
          async: script.async,
        };
        if (script.src) return <script key={script.id} {...props} src={script.src} />;
        return <script key={script.id} {...props} dangerouslySetInnerHTML={{ __html: script.contents ?? '' }} />;
      })}
      <Script
        id="bing-uet-bootstrap"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{ __html: getBingUetBootstrapScriptContents() }}
      />
    </>
  );
}
